import { createTheme } from '@mui/material/styles';

/**
 * Design tokens measured on the reference at 1366x768 (docs/reference-screenshots.md).
 * Change values here to correct visual differences everywhere at once.
 */
export const tokens = {
  headerHeight: 74,
  sidebarWidth: 230,
  topBar: '#388EE5',
  primary: '#0C7AED',
  text: '#333333',
  sidebarText: '#3D3D3D',
  sidebarSubText: '#4D464E',
  cardHeader: '#FAFAFA',
  cardTitle: '#444444',
  border: '#E3E6EA',
  /** Sidebar of the reference: light-blue menu, darker band for the open group. */
  sidebar: {
    menuBg: 'linear-gradient(180deg, #C6E0FF 0%, #E2EFFF 100%)',
    groupBg: 'linear-gradient(90deg, #F6FAFF 0%, #D4E7FF 100%)',
    groupOpenBg: '#76B6FC',
    nestedOpen: '#F5821F',
    nestedBorder: '#5AA5F5',
    bullet: '#444444',
  },
  /** Table header band and the page background behind the white cards. */
  tableHead: '#83AAB9',
  pageBg: '#F4F6F9',
  emptyRow: '#F2DEDE',
  /** Stat tiles and chart series of the dashboard, as measured on the reference. */
  stat: {
    green: '#00A65A',
    red: '#DD4B39',
    yellow: '#F39C12',
    aqua: '#00C0EF',
    fuchsia: '#F012BE',
    blue: '#0073B7',
  },
  chart: { activation: '#7CB5EC', deactivation: '#434348' },
  /** 212Ez's own brand (the logo): deep navy to sky blue, a cyan highlight and a warm spark. */
  brand: {
    navy: '#0A2A66',
    blue: '#0C7AED',
    cyan: '#2FD3F5',
    spark: '#FFB347',
    ink: '#0F1E3D',
  },
};

/** Product name. Set VITE_APP_NAME to rename. */
export const APP_NAME: string = import.meta.env.VITE_APP_NAME || '212Ez';

/** The colours of the frame (top bar, menu, buttons, table headers) that the branding changes. */
export interface ShellColours {
  topBar: string;
  primary: string;
  secondary: string;
  pageBg: string;
  tableHead: string;
  loginBg: string;
  sidebar: typeof tokens.sidebar;
}

/** As measured on the reference. */
export const classicColours: ShellColours = {
  topBar: tokens.topBar,
  primary: tokens.primary,
  secondary: tokens.stat.yellow,
  pageBg: tokens.pageBg,
  tableHead: tokens.tableHead,
  loginBg: `linear-gradient(135deg, ${tokens.topBar} 0%, #1B4F8F 100%)`,
  sidebar: tokens.sidebar,
};

/**
 * 212Ez's branding (owner's site, 2026-10-28): deep navy, the royal blue of the name and an amber
 * accent. Used while the release switch `brand-look` is on.
 */
export const brandColours: ShellColours = {
  topBar: '#0D2163',
  primary: '#1E46B4',
  secondary: '#F2A71B',
  pageBg: '#F3F5FA',
  tableHead: '#24407F',
  loginBg: 'linear-gradient(135deg, #0D2163 0%, #1B3A8F 100%)',
  sidebar: {
    menuBg: 'linear-gradient(180deg, #E6ECF8 0%, #F4F6FB 100%)',
    groupBg: 'linear-gradient(90deg, #FAFBFE 0%, #DCE4F5 100%)',
    groupOpenBg: '#B9CAF0',
    // Darker than the amber itself so the open entry's text stays readable on the light menu.
    nestedOpen: '#B86E00',
    nestedBorder: '#1E46B4',
    bullet: '#444444',
  },
};

export const makeTheme = (colours: ShellColours) =>
  createTheme({
    palette: {
      primary: { main: colours.primary },
      secondary: { main: colours.secondary },
      background: { default: '#FFFFFF', paper: '#FFFFFF' },
      text: { primary: tokens.text },
      divider: tokens.border,
    },
    shape: { borderRadius: 3 },
    typography: {
      fontFamily: 'Calibri, Carlito, "Segoe UI", Arial, sans-serif',
      fontSize: 13,
      h1: { fontSize: 22, fontWeight: 600 },
      h2: { fontSize: 18, fontWeight: 600, color: tokens.cardTitle },
      h3: { fontSize: 16, fontWeight: 600 },
      button: { textTransform: 'none', fontSize: 14 },
    },
    components: {
      MuiButton: { defaultProps: { disableElevation: true, size: 'small' } },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiFormControl: { defaultProps: { size: 'small', fullWidth: true } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: { fontSize: 13, padding: '6px 10px', borderColor: tokens.border },
          head: {
            backgroundColor: colours.tableHead,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            '&:hover': { color: '#FFFFFF' },
            '&.Mui-active': { color: '#FFFFFF', '& .MuiTableSortLabel-icon': { color: '#FFFFFF' } },
          },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 18, fontWeight: 600 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', minHeight: 40, fontSize: 14 } } },
      MuiTabs: { styleOverrides: { root: { minHeight: 40 } } },
    },
  });

export const theme = makeTheme(classicColours);
export const brandTheme = makeTheme(brandColours);
